import React from 'react';
import { useScrollReveal } from './useScrollReveal';

const OUTCOMES = [
  { label: 'A curated theme', sub: 'with colour palette, mood, and concept' },
  { label: 'A categorised shopping list', sub: 'every item you need, organised by type' },
  { label: 'A price estimate per item', sub: 'so you know the cost before you commit' },
  { label: 'A plan you own', sub: 'edit, share, save, and reuse any time' },
];

// Decorative confetti dots — pure CSS, no library
function ConfettiDots() {
  const dots = [
    { x: '8%', y: '12%', size: 6, color: '#C4B5FD', delay: '0s' },
    { x: '92%', y: '8%', size: 8, color: '#FCD34D', delay: '0.4s' },
    { x: '15%', y: '85%', size: 5, color: '#6EE7B7', delay: '0.8s' },
    { x: '85%', y: '80%', size: 7, color: '#F9A8D4', delay: '1.2s' },
    { x: '50%', y: '5%', size: 5, color: '#A5B4FC', delay: '0.2s' },
    { x: '5%', y: '50%', size: 6, color: '#FCA5A5', delay: '0.6s' },
    { x: '95%', y: '50%', size: 5, color: '#6EE7B7', delay: '1s' },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {dots.map((d, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: d.x,
            top: d.y,
            width: d.size,
            height: d.size,
            backgroundColor: d.color,
            opacity: 0.6,
            animationDelay: d.delay,
            animationDuration: `${4 + i * 0.7}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function FinalCTA({ onStartPlanning, isAuthenticated }) {
  const reveal = useScrollReveal();

  return (
    <>
      {/* Outcomes section */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 overflow-hidden w-full max-w-full" style={{ backgroundColor: 'var(--brand-cream)' }}>
        <div className="max-w-4xl mx-auto w-full">
          <div className="text-center mb-12 sr-item" ref={reveal}>
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--brand-violet)' }}>
              The outcome
            </p>
            <h2
              className="font-extrabold leading-tight"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', color: 'var(--brand-charcoal)', letterSpacing: '-0.025em' }}
            >
              When you're done, you'll have:
            </h2>
          </div>

          <div className="sr-stagger grid sm:grid-cols-2 gap-4" ref={reveal}>
            {OUTCOMES.map(({ label, sub }) => (
              <div
                key={label}
                className="sr-item group flex items-start gap-4 bg-white rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                style={{ border: '1.5px solid var(--brand-border)' }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: '#EDE9FE' }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8l3.5 3.5 6.5-7" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--brand-charcoal)' }}>{label}</p>
                  <p className="text-sm" style={{ color: 'var(--brand-muted)' }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Honest footnote */}
          <p className="text-center text-sm mt-8 sr-item" ref={reveal} style={{ color: 'var(--brand-muted)' }}>
            You'll still do the shopping — but we give you the complete list, prices, and plan so you know exactly what to get and where to start.
          </p>
        </div>
      </section>

      {/* Final CTA banner */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 overflow-hidden w-full max-w-full" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-4xl mx-auto w-full">
          <div
            className="relative rounded-3xl overflow-hidden text-center py-12 sm:py-16 px-4 sm:px-16 sr-item"
            ref={reveal}
            style={{ backgroundColor: 'var(--brand-charcoal)' }}
          >
            <ConfettiDots />

            {/* Subtle radial glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(124,58,237,0.2), transparent 60%)' }}
            />

            <div className="relative z-10">
              <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#A78BFA' }}>
                {isAuthenticated ? 'Welcome back' : "Get started — it's free"}
              </p>
              <h2
                className="font-extrabold text-white leading-tight mb-5"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 2.75rem)', letterSpacing: '-0.025em' }}
              >
                {isAuthenticated ? "Your next party plan\nis waiting for you." : "Ready? Your first party plan\ntakes about 60 seconds."}
              </h2>
              <p className="text-base mb-9" style={{ color: '#D6D3D1' }}>
                {isAuthenticated ? "Continue planning your event or start a brand new one with our AI." : "No credit card. No commitment. Get started for free."}
              </p>
              <button
                onClick={onStartPlanning}
                className="group inline-flex items-center gap-2 px-8 py-4 text-sm sm:text-base font-bold rounded-full text-stone-950 bg-white hover:bg-stone-100 transition-all duration-200 hover:scale-[1.02] active:scale-95 shadow-2xl"
              >
                <span>{isAuthenticated ? 'Enter Party Studio' : 'Start Planning Free'}</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
